import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-[#0A1128] text-white grain flex items-center justify-center px-5 py-16">
    <div className="relative z-10 w-full max-w-md">
      <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm uppercase tracking-[0.15em] mb-8 transition-colors duration-200">
        <ArrowLeft className="w-4 h-4" /> Back to site
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <img src="/logo-emblem.png" alt="logo" className="h-14 w-auto" />
        <span className="font-display text-2xl">PATRIOT POWER <span className="text-[#D90429]">USA</span></span>
      </div>
      <h1 className="font-display text-4xl uppercase leading-none">{title}</h1>
      <p className="text-white/50 mt-2 mb-8">{subtitle}</p>
      {children}
    </div>
  </div>
);

export const Input = (props) => (
  <input {...props} className="w-full border border-slate-300 px-4 py-3 rounded-none focus:outline-none focus:border-[#D90429] transition-colors duration-200 mb-5 text-[#0A1128]" />
);

export function MemberLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, ready, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (ready && user) navigate(user.role === "admin" ? "/admin" : "/account", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name || "Patriot"}.`);
      navigate(data.user.role === "admin" ? "/admin" : "/account", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Member Login" subtitle="Access your patriot dashboard.">
      <form onSubmit={submit} data-testid="member-login-form" className="bg-white p-8">
        <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Email</label>
        <Input data-testid="member-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Password</label>
        <Input data-testid="member-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <div className="text-right -mt-2 mb-5">
          <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-[#D90429] font-semibold hover:underline">Forgot password?</Link>
        </div>
        <button type="submit" disabled={loading} data-testid="member-login-submit" className="w-full bg-[#D90429] text-white py-4 uppercase tracking-[0.2em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : "Log In"}
        </button>
        <p className="text-center text-sm text-slate-500 mt-5">
          New here? <Link to="/signup" data-testid="go-signup-link" className="text-[#D90429] font-semibold hover:underline">Create an account</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function MemberSignup() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { user, ready, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (ready && user) navigate(user.role === "admin" ? "/admin" : "/account", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      login(data.token, data.user);
      toast.success("Account created. Welcome, Patriot!");
      navigate("/account", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Join The Ranks" subtitle="Create your member account.">
      <form onSubmit={submit} data-testid="member-signup-form" className="bg-white p-8">
        <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Full Name</label>
        <Input data-testid="signup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Email</label>
        <Input data-testid="signup-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Password</label>
        <Input data-testid="signup-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 8 characters" required />
        <button type="submit" disabled={loading} data-testid="signup-submit" className="w-full bg-[#D90429] text-white py-4 uppercase tracking-[0.2em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : "Create Account"}
        </button>
        <p className="text-center text-sm text-slate-500 mt-5">
          Already a member? <Link to="/login" data-testid="go-login-link" className="text-[#D90429] font-semibold hover:underline">Log in</Link>
        </p>
      </form>
    </AuthShell>
  );
}
